import * as React from 'react';
import { useMergedRefs } from './useMergedRefs.js';
import { useResizeObserver } from './useResizeObserver.js';
import { useLayoutEffect } from './useIsomorphicLayoutEffect.js';
let STARTING_MAX_ITEMS_COUNT = 20;
export const useOverflow = (
  itemsCount,
  disabled = false,
  orientation = 'horizontal',
) => {
  let containerRef = React.useRef(null);
  let [visibleCount, setVisibleCount] = React.useState(() =>
    disabled ? itemsCount : Math.min(itemsCount, STARTING_MAX_ITEMS_COUNT),
  );
  let needsFullRerender = React.useRef(true);
  let [containerSize, setContainerSize] = React.useState(0);
  let previousContainerSize = React.useRef(0);
  let updateContainerSize = React.useCallback(
    ({ width, height }) =>
      setContainerSize('horizontal' === orientation ? width : height),
    [orientation],
  );
  let [resizeRef, observer] = useResizeObserver(updateContainerSize);
  let resizeObserverRef = React.useRef(observer);
  useLayoutEffect(() => {
    if (disabled) setVisibleCount(itemsCount);
    else {
      setVisibleCount(Math.min(itemsCount, STARTING_MAX_ITEMS_COUNT));
      needsFullRerender.current = true;
    }
  }, [containerSize, disabled, itemsCount]);
  let mergedRefs = useMergedRefs(containerRef, resizeRef);
  useLayoutEffect(() => {
    if (!containerRef.current || disabled) {
      resizeObserverRef.current?.disconnect();
      return;
    }
    let dimension = 'horizontal' === orientation ? 'Width' : 'Height';
    let availableSize = containerRef.current[`offset${dimension}`];
    let requiredSize = containerRef.current[`scroll${dimension}`];
    if (availableSize < requiredSize) {
      let avgItemSize = requiredSize / visibleCount;
      let visibleItems = Math.floor(availableSize / avgItemSize);
      setVisibleCount(visibleItems > 0 ? visibleItems : 1);
    } else if (needsFullRerender.current) {
      let childrenSize = Array.from(containerRef.current.children).reduce(
        (sum, child) => sum + child[`offset${dimension}`],
        0,
      );
      let currentVisibleCount =
        visibleCount || Math.min(itemsCount, STARTING_MAX_ITEMS_COUNT);
      let avgItemSize = childrenSize / currentVisibleCount;
      let visibleItems = Math.floor(availableSize / avgItemSize);
      if (!isNaN(visibleItems))
        setVisibleCount(Math.min(itemsCount, 2 * visibleItems));
    }
    needsFullRerender.current = false;
  }, [containerSize, visibleCount, disabled, itemsCount, orientation]);
  useLayoutEffect(() => {
    previousContainerSize.current = containerSize;
  }, [containerSize]);
  return [mergedRefs, visibleCount];
};
